import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Box,
  Chip,
  Alert,
  CircularProgress,
  Paper,
} from '@mui/material';
import {
  School,
  MusicNote,
  Brush,
  TrendingUp,
  ArrowForward,
  Code,
  PersonAdd,
  EmojiEvents,
  MenuBook,
  Psychology,
  Computer,
  Language,
  Calculate,
} from '@mui/icons-material';
import { CourseType } from '../types';
import abacusService, { AbacusRegistrationTile } from '../services/abacusService';
import authService from '../services/authService';
import courseEnrollmentService from '../services/courseEnrollmentService';
import { resolveAbacusTileDisplay, studentAbacusChipLabel } from '../utils/abacusTileDisplay';
import { parentRegistrationPath, studentCoursePath } from '../utils/abacusRegistrationAction';

interface CourseTypeOption {
  type?: CourseType; // undefined = display-only (coming soon), no backend yet
  abacusVariant?: 'JUNIOR' | 'SENIOR';
  name: string;
  description: string;
  icon: React.ReactNode;
  status: 'active' | 'coming-soon';
}

interface CourseCategory {
  id: string;
  title: string;
  subtitle?: string;
  items: CourseTypeOption[];
}

const CATEGORIES: CourseCategory[] = [
  {
    id: 'educational',
    title: 'Educational Courses',
    subtitle: 'Core curriculum for school education',
    items: [
      {
        type: CourseType.SECONDARY_SCHOOL,
        name: 'Secondary School',
        description: 'Full curriculum coverage for secondary education',
        icon: <School sx={{ fontSize: 48, color: 'primary.main' }} />,
        status: 'active',
      },
      {
        type: CourseType.PRIMARY_SCHOOL,
        name: 'Primary School',
        description: 'Comprehensive primary education curriculum',
        icon: <School sx={{ fontSize: 48, color: 'text.disabled' }} />,
        status: 'coming-soon',
      },
    ],
  },
  {
    id: 'competitive',
    title: 'Competitive Exams',
    subtitle: 'Preparation for entrance exams and olympiads',
    items: [
      {
        name: 'Entrance Exams',
        description: 'Prepare for school and college entrance tests',
        icon: <EmojiEvents sx={{ fontSize: 48, color: 'text.disabled' }} />,
        status: 'coming-soon',
      },
      {
        name: 'Olympiads',
        description: 'Math, Science and other subject olympiads',
        icon: <EmojiEvents sx={{ fontSize: 48, color: 'text.disabled' }} />,
        status: 'coming-soon',
      },
      {
        name: 'Govt Job Prep',
        description: 'Focused preparation for competitive assessments',
        icon: <MenuBook sx={{ fontSize: 48, color: 'text.disabled' }} />,
        status: 'coming-soon',
      },
    ],
  },
  {
    id: 'skills-enrichment',
    title: 'Skills & Enrichment',
    subtitle: 'Level-based speciality learning',
    items: [
      {
        abacusVariant: 'JUNIOR',
        name: 'Abacus Maths (Junior)',
        description: 'Levels 1–11 with voice mental drills and Test Weeks',
        icon: <Calculate sx={{ fontSize: 48, color: 'primary.main' }} />,
        status: 'active',
      },
      {
        abacusVariant: 'SENIOR',
        name: 'Abacus Maths (Senior)',
        description: 'Levels 1A–8A — separate Senior programme course',
        icon: <Calculate sx={{ fontSize: 48, color: 'primary.main' }} />,
        status: 'active',
      },
    ],
  },
  {
    id: 'art-recreational',
    title: 'Art & Recreational',
    subtitle: 'Creative and leisure learning',
    items: [
      {
        type: CourseType.PIANO,
        name: 'Piano',
        description: 'Learn piano from beginner to advanced',
        icon: <MusicNote sx={{ fontSize: 48, color: 'text.disabled' }} />,
        status: 'coming-soon',
      },
      {
        type: CourseType.CHESS,
        name: 'Chess',
        description: 'Master the game of chess',
        icon: <TrendingUp sx={{ fontSize: 48, color: 'text.disabled' }} />,
        status: 'coming-soon',
      },
      {
        type: CourseType.MUSIC,
        name: 'Music',
        description: 'Comprehensive music education',
        icon: <MusicNote sx={{ fontSize: 48, color: 'text.disabled' }} />,
        status: 'coming-soon',
      },
      {
        type: CourseType.ART,
        name: 'Art',
        description: 'Explore your creative side',
        icon: <Brush sx={{ fontSize: 48, color: 'text.disabled' }} />,
        status: 'coming-soon',
      },
    ],
  },
  {
    id: 'language-testprep',
    title: 'Language & Test Prep',
    subtitle: 'Languages and standardized test preparation',
    items: [
      {
        name: 'English for Exams',
        description: 'IELTS, TOEFL and academic English',
        icon: <Language sx={{ fontSize: 48, color: 'text.disabled' }} />,
        status: 'coming-soon',
      },
      {
        name: 'SAT / ACT Prep',
        description: 'College admission test preparation',
        icon: <MenuBook sx={{ fontSize: 48, color: 'text.disabled' }} />,
        status: 'coming-soon',
      },
    ],
  },
  {
    id: 'life-skills',
    title: 'Life Skills & Wellness',
    subtitle: 'Personal development and wellbeing',
    items: [
      {
        name: 'Financial Literacy',
        description: 'Managing money and basic finance',
        icon: <Psychology sx={{ fontSize: 48, color: 'text.disabled' }} />,
        status: 'coming-soon',
      },
      {
        name: 'Wellbeing',
        description: 'Mindfulness and emotional wellness',
        icon: <Psychology sx={{ fontSize: 48, color: 'text.disabled' }} />,
        status: 'coming-soon',
      },
    ],
  },
  {
    id: 'professional-stem',
    title: 'Professional & STEM',
    subtitle: 'Career skills and technical learning',
    items: [
      {
        name: 'Coding',
        description: 'Programming and computer science basics',
        icon: <Computer sx={{ fontSize: 48, color: 'text.disabled' }} />,
        status: 'coming-soon',
      },
      {
        name: 'Career Skills',
        description: 'Resume, interviews and workplace readiness',
        icon: <TrendingUp sx={{ fontSize: 48, color: 'text.disabled' }} />,
        status: 'coming-soon',
      },
    ],
  },
];

const CourseTypeSelection: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const childId = searchParams.get('childId');
  const [selectedType, setSelectedType] = useState<CourseType | null>(null);
  const [loading, setLoading] = useState(false);
  const [abacusTiles, setAbacusTiles] = useState<AbacusRegistrationTile[]>([]);
  const [abacusNotice, setAbacusNotice] = useState<string | null>(null);

  useEffect(() => {
    abacusService.fetchAvailableAbacusTiles().then((payload) => {
      setAbacusTiles(payload.tiles ?? []);
    }).catch(() => {
      setAbacusTiles([]);
    });
  }, []);

  const navigateToAbacusCourse = (courseId: number) => {
    const params = new URLSearchParams();
    if (childId) params.set('childId', childId);
    navigate(`${studentCoursePath(courseId)}${params.toString() ? `?${params}` : ''}`);
  };

  const enrollAvailableAbacus = async (courseId: number) => {
    if (childId) {
      navigate(parentRegistrationPath(childId, courseId));
      return;
    }
    const user = authService.getCurrentUser() as { id?: number; email?: string } | null;
    if (!user?.id) {
      setAbacusNotice('Sign in to register for this course.');
      return;
    }
    setLoading(true);
    setAbacusNotice(null);
    try {
      await courseEnrollmentService.enrollUserInCourse(user.id, courseId, user.email || 'self');
      navigate(studentCoursePath(courseId));
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Enrollment failed. Please try again.';
      setAbacusNotice(message);
    } finally {
      setLoading(false);
    }
  };

  const handleTypeSelect = (option: CourseTypeOption) => {
    if (loading) {
      return;
    }
    if (option.abacusVariant) {
      setAbacusNotice(null);
      const tile = abacusTiles.find((t) => t.variant === option.abacusVariant);
      const display = resolveAbacusTileDisplay(tile);
      if (display === 'enrolled' && tile?.enrolledCourse) {
        navigateToAbacusCourse(tile.enrolledCourse.id);
        return;
      }
      if (display === 'available' && tile?.courses?.[0]) {
        void enrollAvailableAbacus(tile.courses[0].id);
        return;
      }
      if (display === 'teaser' && tile?.comingSoonTeaser) {
        setAbacusNotice(
          `${tile.comingSoonTeaser.name} is coming soon — enrollment opens when the course is published.`
        );
        return;
      }
      setAbacusNotice(`${option.name} is coming soon — no course is published yet.`);
      return;
    }
    if (option.status === 'active' && option.type !== undefined) {
      setSelectedType(option.type);
    }
  };

  const handleCourseCodeRegistration = () => {
    if (!selectedType) return;
    setLoading(true);
    // Navigate to course code registration
    const params = childId ? `?childId=${childId}` : '';
    navigate(`/register-course-code${params}`);
  };

  const handleSelfRegistration = () => {
    if (!selectedType) return;
    setLoading(true);
    // Navigate to self-registration flow
    const params = childId ? `?childId=${childId}` : '';
    navigate(`/register-course-self${params}`);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={0} sx={{ p: 4, mb: 4 }}>
        <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
          {childId ? 'Register Child for Course' : 'Register for Course'}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Select a course type to get started. Choose from our available courses or explore what's coming soon.
        </Typography>

        {/* Course Type Selection by Category */}
        <Box sx={{ mb: 4 }}>
          {CATEGORIES.map((category) => (
            <Box key={category.id} sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                {category.title}
              </Typography>
              {category.subtitle && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {category.subtitle}
                </Typography>
              )}
              <Grid container spacing={3}>
                {category.items.map((option) => {
                  const isSelected = option.type !== undefined && selectedType === option.type;
                  const abacusTile = option.abacusVariant
                    ? abacusTiles.find((t) => t.variant === option.abacusVariant)
                    : undefined;
                  const abacusDisplay = option.abacusVariant
                    ? resolveAbacusTileDisplay(abacusTile)
                    : null;
                  const abacusDisplayStatus = option.abacusVariant
                    ? abacusDisplay === 'available' || abacusDisplay === 'enrolled'
                      ? 'active'
                      : 'coming-soon'
                    : option.status;
                  const isClickable = option.abacusVariant
                    ? abacusDisplay === 'available' || abacusDisplay === 'enrolled'
                    : abacusDisplayStatus === 'active' && option.type !== undefined;
                  const abacusChipLabel = abacusDisplay
                    ? studentAbacusChipLabel(abacusDisplay)
                    : undefined;
                  const abacusSubtitle = option.abacusVariant
                    ? abacusDisplay === 'teaser'
                      ? `${abacusTile?.comingSoonTeaser?.name ?? option.name} — publishing soon`
                      : abacusDisplay === 'enrolled'
                      ? `${abacusTile?.enrolledCourse?.name ?? option.name} — open your course`
                      : option.description
                    : option.description;
                  return (
                    <Grid item xs={12} sm={6} md={4} key={option.type ?? option.abacusVariant ?? option.name}>
                      <Card
                        onClick={() => handleTypeSelect(option)}
                        sx={{
                          cursor: isClickable ? 'pointer' : 'default',
                          border: isSelected ? 2 : 1,
                          borderColor:
                            isSelected
                              ? 'primary.main'
                              : isClickable
                              ? 'grey.300'
                              : 'grey.200',
                          opacity: abacusDisplayStatus === 'coming-soon' ? 0.6 : 1,
                          height: '100%',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: isClickable ? 'translateY(-4px)' : 'none',
                            boxShadow: isClickable ? 4 : 1,
                          },
                        }}
                      >
                        <CardContent sx={{ textAlign: 'center', py: 3 }}>
                          <Box sx={{ mb: 2 }}>{option.icon}</Box>
                          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                            {option.name}
                          </Typography>
                          <Chip
                            label={option.abacusVariant ? abacusChipLabel : abacusDisplayStatus === 'active' ? 'Available' : 'Coming Soon'}
                            color={abacusDisplayStatus === 'active' ? 'success' : 'default'}
                            size="small"
                            sx={{ mb: 2 }}
                          />
                          <Typography variant="body2" color="text.secondary">
                            {abacusSubtitle}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          ))}
        </Box>

        {/* Registration Options (only shown when Secondary School is selected) */}
        {selectedType === CourseType.SECONDARY_SCHOOL && (
          <Box>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Choose Registration Method
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card
                  sx={{
                    border: 1,
                    borderColor: 'primary.main',
                    height: '100%',
                    '&:hover': {
                      boxShadow: 4,
                    },
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', py: 4 }}>
                    <Code sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      Register with Course Code
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      If you have received a course code from your teacher or school, use this option to register.
                    </Typography>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleCourseCodeRegistration}
                      disabled={loading}
                      endIcon={loading ? <CircularProgress size={20} /> : <ArrowForward />}
                    >
                      Use Course Code
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card
                  sx={{
                    border: 1,
                    borderColor: 'primary.main',
                    height: '100%',
                    '&:hover': {
                      boxShadow: 4,
                    },
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', py: 4 }}>
                    <PersonAdd sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      Self-Registration
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Register yourself by selecting your school, council, and class. Perfect for independent learners.
                    </Typography>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleSelfRegistration}
                      disabled={loading}
                      endIcon={loading ? <CircularProgress size={20} /> : <ArrowForward />}
                    >
                      Self-Register
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}

        {selectedType && selectedType !== CourseType.SECONDARY_SCHOOL && selectedType !== CourseType.ABACUS_MATHS && (
          <Alert severity="info" sx={{ mt: 3 }}>
            This course type is coming soon. Please check back later or select Secondary School to get started.
          </Alert>
        )}

        {abacusNotice && (
          <Alert severity="info" sx={{ mt: 3 }}>
            {abacusNotice}
          </Alert>
        )}

      </Paper>
    </Container>
  );
};

export default CourseTypeSelection;

